import axios, {AxiosRequestConfig, AxiosResponse} from 'axios'
import process from 'process'
import {
  showBanModal,
  showModalOnHold,
  showDownTimeModal,
  getLocationPermission,
  getAPIBaseUrl,
  logout,
  showLogoutModal,
} from 'utils'
import base64 from 'base-64'
import {ServiceResponses, UserData} from 'types'
import {API_OAUTH_REFRESH_TOKEN} from 'consts'
import {reduxUpdateDispatcher, REDUX_STORE} from 'lib/redux'
import {channelRefreshToken, refreshActivity} from './ServiceBroadcast'

function handleBanModal(status: number, message: string) {
  showBanModal(status, message)
}

function handleLogoutModal() {
  logout()
  showLogoutModal()
}

function handleDownTimeModal() {
  showDownTimeModal(true)
}

export function handleRequestOnFulfilled(request: AxiosRequestConfig) {
  const token = REDUX_STORE.getState().user?.access_token
  let latitude = REDUX_STORE.getState().user?.latitude
  let longitude = REDUX_STORE.getState().user?.longitude

  if (!(latitude && longitude))
    getLocationPermission(
      () => {},
      () =>
        navigator.geolocation.getCurrentPosition(
          (position) => {
            longitude = position.coords.longitude
            latitude = position.coords.latitude
          },
          () => {},
          {enableHighAccuracy: true},
        ),
    )

  if (token && request.headers) {
    request.headers.Authorization = `Bearer ${token}`
  }

  return request
}

async function handleRefreshAccessToken() {
  const user = 'web'
  const password = process.env.AUTH_CLIENT_PASSWORD
  const refreshToken =
    REDUX_STORE.getState().user?.refresh_token ||
    REDUX_STORE.getState().lastUserState.refreshToken

  if (refreshToken) {
    await axios
      .post(
        getAPIBaseUrl() + API_OAUTH_REFRESH_TOKEN,
        {
          code_verifier: process.env.AUTH_CODE_VERIFIER,
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
        },
        {
          headers: {
            Authorization: `Basic ${base64.encode(`${user}:${password}`)}`,
          },
        },
      )
      .then((value) => {
        if (value.status === 200) {
          const {result} = value.data as ServiceResponses<UserData>

          reduxUpdateDispatcher('user', result)
        }
      })
      .catch((value) => {
        if (
          typeof value === 'object' &&
          value.response.status === 401 &&
          (value.response.data.detail.refresh_token ===
            'expired refresh token' ||
            value.response.data.detail.refresh_token ===
              'invalid refresh token')
        ) {
          reduxUpdateDispatcher('lastUserState', {isTokenExpired: true})
          handleLogoutModal()
        }
      })
  }
}

export function handleServiceResponseInterceptor(response: AxiosResponse) {
  const accToken = REDUX_STORE.getState().user?.access_token
  const {data, status, config} = response

  if (
    status === 403 &&
    (data.message === 'Banned' || data.message === 'Underage')
  ) {
    handleBanModal(status, data.message)
  } else if (status === 403 && data.message === 'Duplicate Account') {
    accToken
      ? window.location.replace('/similar-identity')
      : showModalOnHold(true)
  } else if (status === 403 && data.message === 'Feature Is Disabled') {
    handleDownTimeModal()
  } else if (
    status === 423 &&
    (data.message === 'Suspended' || data.message === 'Attempt Login Suspended')
  ) {
    handleBanModal(status, data.message)
  } else if (
    status === 401 &&
    (data?.detail?.access_token === 'expired access token' ||
      data?.detail?.access_token === 'invalid access token')
  ) {
    const configAccessToken = config.headers?.Authorization.toString().slice(7)

    const isAccessTokenUpdated = () =>
      !!configAccessToken &&
      !!REDUX_STORE.getState().user?.access_token &&
      configAccessToken !== REDUX_STORE.getState().user?.access_token

    /** If the access token changes during the request, then re-send the request using the new token. */
    if (config.headers && isAccessTokenUpdated()) {
      config.headers.Authorization = `Bearer ${
        REDUX_STORE.getState().user?.access_token
      }`
      return axios(config).then(
        (value) => value,
        (reason) => reason.response,
      )
    }

    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve) => {
      /**
       * Retry is only called after the refresh token process is complete.
       * The refresh trigger is called only once, and after the refresh token is completed, all queued requests will be retried.
       */
      const onRetry = () => {
        if (config.headers && isAccessTokenUpdated()) {
          config.headers.Authorization = `Bearer ${
            REDUX_STORE.getState().user?.access_token
          }`
          resolve(
            axios(config).then(
              (value) => value,
              (reason) => reason.response,
            ),
          )
        } else {
          resolve(response)
        }
      }

      refreshActivity.retryQueue.push(onRetry)

      /**
       * When refresh_id is not set, it means the refresh is not active,
       * so trigger the refresh token.
       */
      if (!refreshActivity.refresh_id) {
        channelRefreshToken.postMessage({is_active: true}, true)
        await handleRefreshAccessToken()
        channelRefreshToken.postMessage({is_active: false}, true)
      }
    })
  }

  return response
}
